/* ShareVictoryCard — canvas-generated victory card PNG.
 *
 * Renders a 720×1280 (9:16) card in-memory with the winner name,
 * opponent name, rounds, arena, and coin reward. The user can either
 * use the native Web Share API (mobile) or download the PNG directly.
 *
 * Zero deps — plain HTMLCanvasElement + toBlob.
 */
import { useEffect, useRef, useState } from 'react';
import { Download, Share2, X, Trophy } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { loadBrandLogo, drawBrandLogo } from '../../lib/brandLogo';

const CARD_W = 720;
const CARD_H = 1280;
const APP_NAME = 'WildQuest';

async function drawImage(ctx, url, x, y, w, h) {
  return new Promise((resolve) => {
    if (!url) return resolve(false);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // object-fit: cover
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;
      const ir = iw / ih;
      const tr = w / h;
      let sx = 0, sy = 0, sw = iw, sh = ih;
      if (ir > tr) { sw = ih * tr; sx = (iw - sw) / 2; }
      else         { sh = iw / tr; sy = (ih - sh) / 2; }
      ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
      resolve(true);
    };
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

/* Draw the victory card onto the supplied canvas. */
async function paintCard(canvas, data) {
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, CARD_W, CARD_H);

  // Background — deep-forest gradient
  const g = ctx.createLinearGradient(0, 0, 0, CARD_H);
  g.addColorStop(0, '#062018');
  g.addColorStop(0.55, '#0A2E22');
  g.addColorStop(1, '#04120C');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // Golden rim glow
  const rg = ctx.createRadialGradient(CARD_W/2, 420, 60, CARD_W/2, 420, 500);
  rg.addColorStop(0, 'rgba(253, 224, 71, 0.35)');
  rg.addColorStop(1, 'rgba(253, 224, 71, 0)');
  ctx.fillStyle = rg;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // Header — brand mark + label
  const brandLogo = await loadBrandLogo().catch(() => null);
  drawBrandLogo(ctx, brandLogo, 60, 60, 48);
  ctx.fillStyle = '#E9C46A';
  ctx.font = '700 22px "Inter", "system-ui", sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText(APP_NAME.toUpperCase(), brandLogo ? 122 : 60, 90);

  ctx.font = '400 18px "Inter", "system-ui", sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.textAlign = 'right';
  ctx.fillText('BATTLE RECAP', CARD_W - 60, 90);

  // Big "VICTORY" or "DEFEAT"
  ctx.textAlign = 'center';
  const won = data.won !== false;
  ctx.fillStyle = won ? '#FDE68A' : '#F87171';
  ctx.font = '900 110px "Georgia", "Times", serif';
  ctx.fillText(won ? 'VICTORY' : 'DEFEAT', CARD_W / 2, 240);

  // Winner name
  ctx.fillStyle = '#FFFFFF';
  ctx.font = '700 54px "Georgia", "Times", serif';
  wrapText(ctx, data.winnerName || 'Champion', CARD_W / 2, 320, CARD_W - 120, 60);

  // Portrait area
  const portraitY = 400;
  const portraitH = 460;
  ctx.save();
  const path = new Path2D();
  path.roundRect(60, portraitY, CARD_W - 120, portraitH, 40);
  ctx.clip(path);
  const drewImg = await drawImage(ctx, data.winnerPicture, 60, portraitY, CARD_W - 120, portraitH);
  if (!drewImg) {
    // Fallback tile
    ctx.fillStyle = '#122';
    ctx.fillRect(60, portraitY, CARD_W - 120, portraitH);
    ctx.fillStyle = 'rgba(233, 196, 106, 0.85)';
    ctx.font = '900 96px "Georgia", serif';
    ctx.textAlign = 'center';
    ctx.fillText('🏆', CARD_W / 2, portraitY + portraitH / 2 + 40);
  }
  ctx.restore();

  // Portrait border
  ctx.strokeStyle = 'rgba(233, 196, 106, 0.7)';
  ctx.lineWidth = 3;
  const rp = new Path2D();
  rp.roundRect(60, portraitY, CARD_W - 120, portraitH, 40);
  ctx.stroke(rp);

  // Vs banner
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(255,255,255,0.65)';
  ctx.font = '600 22px "Inter", sans-serif';
  const opLabel = data.loserName ? `defeated ${data.loserName}` : 'took the arena';
  ctx.fillText(opLabel, CARD_W / 2, 910);

  // Rounds / arena strip
  ctx.font = '700 20px "Inter", sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  const rounds = data.rounds || '—';
  const arena = (data.arena || 'wild arena').toUpperCase();
  ctx.fillText(`ROUNDS ${rounds}  ·  ${arena}`, CARD_W / 2, 960);

  // Coin reward pill
  if (data.coinsEarned && data.coinsEarned > 0) {
    const pw = 260;
    const px = (CARD_W - pw) / 2;
    const py = 1000;
    ctx.fillStyle = 'rgba(253, 224, 71, 0.16)';
    ctx.strokeStyle = 'rgba(253, 224, 71, 0.7)';
    ctx.lineWidth = 2;
    const pill = new Path2D();
    pill.roundRect(px, py, pw, 64, 32);
    ctx.fill(pill);
    ctx.stroke(pill);
    ctx.fillStyle = '#FDE68A';
    ctx.font = '800 32px "Inter", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`+${data.coinsEarned} WildCoins`, CARD_W / 2, py + 43);
  }

  // Footer — timestamp + brand watermark (subtle logo behind the site url)
  ctx.fillStyle = 'rgba(255,255,255,0.42)';
  ctx.font = '400 18px "Inter", sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText(new Date().toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  }), 60, CARD_H - 60);
  // Ghosted logo in the footer — reinforces brand without competing
  // with the primary "wildquest.app" typeset.
  drawBrandLogo(ctx, brandLogo, CARD_W - 250, CARD_H - 96, 40, 0.55);
  ctx.textAlign = 'right';
  ctx.fillStyle = 'rgba(233, 196, 106, 0.65)';
  ctx.fillText('wildquest.app', CARD_W - 60, CARD_H - 60);
}

/* Word-wrap helper. */
function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = String(text || '').split(' ');
  let line = '';
  let cy = y;
  for (const word of words) {
    const test = line + word + ' ';
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line.trim(), x, cy);
      line = word + ' ';
      cy += lineHeight;
    } else {
      line = test;
    }
  }
  ctx.fillText(line.trim(), x, cy);
}

export default function ShareVictoryCard({ open, onClose, data }) {
  const canvasRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [shareSupported] = useState(() => (
    typeof navigator !== 'undefined' && typeof navigator.share === 'function'
  ));

  useEffect(() => {
    if (!open || !canvasRef.current || !data) return;
    canvasRef.current.width = CARD_W;
    canvasRef.current.height = CARD_H;
    (async () => {
      setBusy(true);
      await paintCard(canvasRef.current, data);
      setPreviewUrl(canvasRef.current.toDataURL('image/png'));
      setBusy(false);
    })();
  }, [open, data]);

  const download = () => {
    if (!canvasRef.current) return;
    canvasRef.current.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `wildquest-victory-${stamp}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  const share = async () => {
    if (!canvasRef.current) return;
    canvasRef.current.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], 'wildquest-victory.png', { type: 'image/png' });
      try {
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: 'WildQuest Victory!',
            text: `${data?.winnerName || 'Explorer'} just won a WildQuest battle!`,
          });
        } else {
          download();
        }
      } catch {
        // User canceled — silent.
      }
    }, 'image/png');
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
          data-testid="share-victory-card"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-sm w-full flex flex-col items-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300 flex items-center gap-1.5">
                <Trophy className="w-3 h-3" /> Share your win
              </p>
              <button onClick={onClose} data-testid="share-card-close"
                className="w-8 h-8 rounded-full glass border border-white/10 flex items-center justify-center hover:bg-white/10"
                aria-label="Close">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="relative w-full aspect-[9/16] rounded-2xl overflow-hidden border border-amber-400/30 shadow-2xl bg-black/60">
              {previewUrl && (
                <img src={previewUrl} alt="Victory card"
                  className="w-full h-full object-contain"
                  data-testid="share-card-preview" />
              )}
              {busy && (
                <div className="absolute inset-0 flex items-center justify-center text-xs uppercase tracking-widest text-amber-200/70">
                  Rendering…
                </div>
              )}
            </div>

            <canvas ref={canvasRef} className="hidden" aria-hidden="true" />

            <div className="w-full grid grid-cols-2 gap-2">
              <button onClick={download} data-testid="share-card-download"
                className="px-4 py-3 rounded-full glass border border-white/15 text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-white/10">
                <Download className="w-3.5 h-3.5" /> Download
              </button>
              <button onClick={share} data-testid="share-card-share"
                disabled={!previewUrl}
                className="px-4 py-3 rounded-full bg-amber-400 text-black text-xs uppercase tracking-widest font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                <Share2 className="w-3.5 h-3.5" /> {shareSupported ? 'Share' : 'Copy image'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
